import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import PlayerSearchBar from "./PlayerSearchBar";
import SplitsPercentiles from "./SplitsPercentiles";
import { useAuth } from "./AuthContext";

export default function SplitsPage(props: any) {
  const { id } = useParams<{ id?: string }>();
  const { loading: authLoading } = useAuth();

  const [playerData, setPlayerData] = useState<any>(null);
  const [playerDataMap, setPlayerDataMap] = useState<any>(null);
  const [playerInfo, setPlayerInfo] = useState<any>({});
  const [seasonLevel, setSeasonLevel] = useState("2026_AAA");
  const [seasonLevels, setSeasonLevels] = useState<string[]>([]);
  const [qualifier, setQualifier] = useState("1");
  const [ageRange, setAgeRange] = useState<number[]>([16, 40]);
  const [loadingPlayer, setLoadingPlayer] = useState(false);

  const season = seasonLevel.split("_")[0];
  const level = seasonLevel.split("_")[1];

  // Only 2026 has splits data for now
  const splitsSeasons = ["2026"];

  useEffect(() => {
    if (!id) return;
    setLoadingPlayer(true);
    const base = props.prod ? "https://oriolebird.pythonanywhere.com" : "";
    fetch(`${base}/player/${id}`)
      .then((r) => r.json())
      .then((data1) => {
        const available = Object.keys(data1).reverse();
        setSeasonLevels(available);
        setPlayerDataMap(data1);
        const current = available.includes(seasonLevel) ? seasonLevel : available[0];
        setSeasonLevel(current);
        setPlayerData(data1[current]);

        // Fetch player info for team colours
        fetch(props.prod ? "https://oriolebird.pythonanywhere.com/player-info/" : "/player-info/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ playerUrlProp: data1[available[0]]["UPURL"] }),
        })
          .then((r) => r.json())
          .then((d) => setPlayerInfo(d))
          .catch(() => {});
        setLoadingPlayer(false);
      })
      .catch(() => { setPlayerData(null); setLoadingPlayer(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, props.prod]);

  // When seasonLevel changes after load, update playerData from already-fetched data
  useEffect(() => {
    if (!playerDataMap) return;
    setPlayerData(playerDataMap[seasonLevel] ?? playerDataMap[Object.keys(playerDataMap)[0]]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonLevel]);

  if (authLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  const position = playerData?.Position === "P" ? "P" : "H";

  return (
    <div style={{ backgroundColor: "#E0FBFC", minHeight: "80vh", padding: "16px" }}>
      <title>Splits Percentiles – Prospect Savant</title>

      {/* Search */}
      <Box sx={{ maxWidth: 500, mx: "auto", mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" textAlign="center" mb={1}>
          Splits Percentiles
        </Typography>
        <PlayerSearchBar prod={props.prod} urlPrefix="/plus/splits" placeholder="Search for a player…" />
      </Box>

      {!id && (
        <Typography textAlign="center" color="text.secondary">
          Search for a player above to view their split percentiles.
        </Typography>
      )}

      {id && loadingPlayer && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {id && !loadingPlayer && playerData && (
        <>
          {/* Filters */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "center", mb: 3 }}>
            <FormControl size="small">
              <InputLabel>Season / Level</InputLabel>
              <Select
                value={seasonLevel}
                label="Season / Level"
                onChange={(e: SelectChangeEvent) => setSeasonLevel(e.target.value)}
                sx={{ minWidth: 140 }}
              >
                {seasonLevels
                  .filter((sl) => splitsSeasons.includes(sl.split("_")[0]))
                  .map((sl) => (
                    <MenuItem key={sl} value={sl}>
                      {sl.replace("_", " ")}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel>Pitch Qualifier</InputLabel>
              <Select
                value={qualifier}
                label="Pitch Qualifier"
                onChange={(e: SelectChangeEvent) => setQualifier(e.target.value)}
                sx={{ width: 110 }}
              >
                {[1, 10, 25, 50, 100, 150, 200, 300, 500].map((v) => (
                  <MenuItem key={v} value={v}>{v}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ minWidth: 220, px: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Age Range
              </Typography>
              <Slider
                value={ageRange}
                onChange={(_e, v) => setAgeRange(v as number[])}
                valueLabelDisplay="auto"
                min={16}
                max={40}
                disableSwap
                size="small"
              />
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Splits card */}
          <Grid container justifyContent="center">
            <Grid item>
              <SplitsPercentiles
                isDesktop={props.isDesktop}
                id={id}
                position={position}
                level={level}
                season={season}
                qualifier={qualifier}
                ageRange={ageRange}
                prod={props.prod}
                playerInfoProp={playerInfo}
                playerName={playerData?.name}
                abbName={playerData?.MLB_AbbName}
              />
            </Grid>
          </Grid>
        </>
      )}
    </div>
  );
}
